/**
 * Aviso de assinatura no painel
 *
 * Exibe ao dono/gerente do estabelecimento quantos dias restam do período
 * de teste gratuito, ou alerta de pagamento pendente (PAST_DUE), com atalho
 * para os planos e para o time comercial no website.
 *
 * @author Equipe VendeAi
 * @since 1.1.0
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, AlertTriangle, X } from 'lucide-react';
import { api } from '@services/api';
import { useAuth } from '@hooks/useAuth';

interface SubscriptionSummary {
  status: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'EXPIRED' | 'CANCELLED';
  endsAt: string | null;
  daysLeft: number | null;
  plan: { name: string; isTrial: boolean } | null;
}

const DISMISS_KEY = 'vendeai_subscription_banner_dismissed';
const MANAGER_ROLES = new Set(['OWNER', 'MANAGER', 'ADMIN']);

export function SubscriptionBanner() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const canSee = !!user && MANAGER_ROLES.has(user.role);

  useEffect(() => {
    if (!canSee || dismissed) return;
    let cancelled = false;
    api
      .get('/auth/me')
      .then(res => {
        if (!cancelled) setSubscription(res.data?.data?.subscription ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [canSee, dismissed]);

  if (!canSee || dismissed || !subscription) return null;

  const isTrial = subscription.status === 'TRIAL';
  const isPastDue = subscription.status === 'PAST_DUE';
  if (!isTrial && !isPastDue) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* armazenamento indisponível: oculta apenas nesta renderização */
    }
  };

  const days = subscription.daysLeft ?? 0;
  const urgent = isPastDue || days <= 5;

  const message = isPastDue
    ? 'Não conseguimos processar o pagamento da sua assinatura. Regularize para evitar a suspensão da loja.'
    : days === 0
      ? 'Seu período de teste termina hoje.'
      : `Você está no período de teste grátis: ${days === 1 ? 'resta 1 dia' : `restam ${days} dias`}${
          subscription.plan && !subscription.plan.isTrial
            ? ` do plano ${subscription.plan.name}`
            : ''
        }.`;

  return (
    <div
      role="status"
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 text-sm md:px-6 ${
        urgent
          ? 'bg-amber-500 text-amber-950'
          : 'bg-gradient-to-r from-secondary-900 to-secondary-700 text-white'
      }`}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {urgent ? (
          <AlertTriangle size={16} className="shrink-0" />
        ) : (
          <Sparkles size={16} className="shrink-0" />
        )}
        <span>{message}</span>
      </span>
      <span className="flex items-center gap-2">
        <Link
          to="/assinatura"
          className={`rounded-lg px-3 py-1 text-xs font-semibold ${urgent ? 'bg-amber-950 text-white' : 'bg-white text-secondary-900'}`}
        >
          {isPastDue ? 'Regularizar' : 'Assinar agora'}
        </Link>
        <Link
          to="/#contato"
          className={`rounded-lg px-2 py-1 text-xs font-semibold underline-offset-2 hover:underline ${
            urgent ? 'text-amber-950 hover:text-amber-950' : 'text-white hover:text-white'
          }`}
        >
          Falar com o time
        </Link>
        <button
          type="button"
          onClick={dismiss}
          className="rounded p-1 opacity-80 hover:opacity-100"
          aria-label="Fechar aviso"
        >
          <X size={16} />
        </button>
      </span>
    </div>
  );
}
